/**
 * One Escape path for the landing header and the news strip.
 *
 * The header owns whether the narrow menu is open. The strip registers the
 * function that dismisses it. Neither component adds its own document
 * listener: a second one would let Escape close the menu and the strip in
 * the same keypress.
 *
 * Plain Escape only. If the menu is open, Escape closes the menu and leaves
 * the strip up. Otherwise Escape runs the registered strip dismiss.
 */

export const LANDING_NEWS_STRIP_DISMISSED_KEY = "acertei:landing:newsStripDismissed";

/** Value written when the visitor dismisses the strip. Any stored value counts. */
export const LANDING_NEWS_STRIP_DISMISSED_VALUE = "1";

const WORDMARK_SELECTOR = "#landing-header a.acertei-wordmark";

let menuOpen = false;
let stripDismiss: (() => void) | null = null;
const listeners = new Set<() => void>();

function isPlainEscape(event: KeyboardEvent): boolean {
  return (
    event.key === "Escape" &&
    !event.repeat &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.altKey &&
    !event.shiftKey
  );
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

function onDocumentKeyDown(event: KeyboardEvent): void {
  if (!isPlainEscape(event)) return;
  if (menuOpen) {
    event.preventDefault();
    setLandingMenuOpen(false);
    return;
  }
  if (!stripDismiss) return;
  event.preventDefault();
  stripDismiss();
}

function ensureBound(): void {
  if (typeof document === "undefined") return;
  const scope = document as Document & { acerteiLandingEscapeBound?: boolean };
  if (scope.acerteiLandingEscapeBound) return;
  scope.acerteiLandingEscapeBound = true;
  document.addEventListener("keydown", onDocumentKeyDown);
}

export function isLandingMenuOpen(): boolean {
  return menuOpen;
}

export function setLandingMenuOpen(open: boolean): void {
  ensureBound();
  if (menuOpen === open) return;
  menuOpen = open;
  notify();
}

export function subscribeLandingMenu(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Register the strip's dismiss, or null when the strip is not on screen. */
export function registerLandingStripDismiss(dismiss: (() => void) | null): void {
  ensureBound();
  stripDismiss = dismiss;
}

/** Move focus to the header wordmark link. The header renders that link. */
export function focusLandingWordmark(): void {
  if (typeof document === "undefined") return;
  document.querySelector<HTMLElement>(WORDMARK_SELECTOR)?.focus();
}

export function readLandingNewsStripDismissed(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(LANDING_NEWS_STRIP_DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

export function writeLandingNewsStripDismissed(): void {
  try {
    sessionStorage.setItem(
      LANDING_NEWS_STRIP_DISMISSED_KEY,
      LANDING_NEWS_STRIP_DISMISSED_VALUE,
    );
  } catch {
    // Private mode can reject the write. The strip still closes for this view.
  }
}
